import React, { useEffect, useState } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import { Button, Row, Col } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { FormikRangePicker, FormikSelect } from '../../components';
import { debounceIndexEmployeesRequest } from '../../requests/employees';
import { debounceIndexCompanyTaskRequest } from '../../requests/companyTasks';

const ALL_SELECTED_LABEL = 'Todos';

const CompanyTaskFilter = props => {
  const { errors, touched, setFieldValue, values } = props;
  const [employees, setEmployees] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const dispatch = useDispatch();

  const fetchEmployees = () => {
    debounceIndexEmployeesRequest({
      dispatch,
      params: {
        active: true,
        sort_column: 'name',
        is_dt: false,
        display_length: 100
      },
      successCallback: response => {
        const dataToGroupSearch = [{ id: 'Todos', label: 'Todos', value: 'Todos' }, ...response.data.data];
        setEmployees(response.data.data.length > 0 ? dataToGroupSearch : []);
      }
    });
  };

  const hasAllSelection = (groupSelection = []) => {
    return groupSelection.some(option => option.value === ALL_SELECTED_LABEL);
  };

  const handleDisabledSelection = (options, selectedOptions) => {
    if (hasAllSelection(selectedOptions)) {
      return true;
    }
    if (selectedOptions.length === 0) return false;
    return options.value === ALL_SELECTED_LABEL;
  };

  const handleNewMulti = (data, field, allowEmpty = false) => {
    const newData = data.map(element => element.value);
    if (newData.length && newData.includes(ALL_SELECTED_LABEL)) {
      newData.push('');
      return;
    }

    if (allowEmpty && !newData.length) newData.push('');
    setFieldValue(field, newData);
  };

  const fetchTasks = dateRange => {
    if (!dateRange || dateRange.length < 2 || !dateRange[0] || !dateRange[1]) return;

    try {
      setLoadingTasks(true);
      let startDate = dateRange[0];
      let endDate = dateRange[1];

      // Ensure dates are converted to expected string format DD-MM-YYYY
      if (typeof startDate.format === 'function') startDate = startDate.format('DD-MM-YYYY');
      else if (typeof startDate === 'string') startDate = startDate.replace(/\//g, '-');

      if (typeof endDate.format === 'function') endDate = endDate.format('DD-MM-YYYY');
      else if (typeof endDate === 'string') endDate = endDate.replace(/\//g, '-');

      if (!startDate || !endDate) {
        setLoadingTasks(false);
        return;
      }

      const rangeParams = `${startDate},${endDate}`;
      debounceIndexCompanyTaskRequest({
        dispatch,
        params: {
          display_length: 1000,
          date_range: rangeParams
        },
        successCallback: response => {
          const mappedTasks = response.data.data.map(task => ({
            id: task.id,
            label: task.title,
            value: task.title
          }));
          const uniqueTasks = Array.from(new Map(mappedTasks.map(item => [item.value, item])).values());
          setTasks(uniqueTasks);
        },
        callback: () => setLoadingTasks(false)
      });
    } catch (error) {
      setLoadingTasks(false);
    }
  };

  useEffect(() => {
    if (values.dateRange && values.dateRange.length === 2 && values.dateRange[0] && values.dateRange[1]) {
      fetchTasks(values.dateRange);
    } else {
      setTasks([]);
      setFieldValue('filterTitle', '');
    }
    // eslint-disable-next-line
  }, [values.dateRange]);

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line
  }, []);

  const priorityOptions = [
    { label: 'Alta', value: 2 },
    { label: 'Media', value: 1 },
    { label: 'Baja', value: 0 }
  ];

  const statusOptions = [
    { label: 'Terminada', value: 1 },
    { label: 'Pendiente', value: 0 }
  ];

  return (
    <Form className="absence-filter-form pt-2 px-3 mt-2">
      <Row>
        <Col xs={12} md={3}>
          <Field name="dateRange">
            {({ field }) => (
              <FormikRangePicker
                {...field}
                name={field.name}
                abbr
                label="Periodo (Fecha de Inicio)"
                required
                startDateName="startDate"
                endDateName="endDate"
                startDate={values.startDate}
                endDate={values.endDate}
                showClearDates
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
                appendToBody
                repositionPickerOnUpdate
              />
            )}
          </Field>
        </Col>
        <Col xs={12} md={3}>
          <Field name="filterEmployee">
            {({ field }) => (
              <FormikSelect
                {...field}
                label="Colaboradores:"
                isMulti
                options={employees}
                placeholder=""
                onChange={data => {
                  handleNewMulti(data || [], 'filter_employee', true);
                  setFieldValue(field.name, data || []);
                }}
                isOptionDisabled={handleDisabledSelection}
                isClearable
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col xs={12} md={3}>
          <Field name="filterTitle">
            {({ field }) => (
              <FormikSelect
                {...field}
                label="Tarea:"
                options={tasks}
                placeholder=""
                onChange={data => setFieldValue(field.name, data ? data.value : '')}
                isClearable
                isDisabled={!values.dateRange || values.dateRange.length !== 2 || loadingTasks}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col xs={12} md={3}>
          <Field name="filterPriority">
            {({ field }) => (
              <FormikSelect
                {...field}
                label="Prioridad:"
                options={priorityOptions}
                placeholder=""
                onChange={data => setFieldValue(field.name, data ? data.value : '')}
                isClearable
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col xs={12} md={3}>
          <Field name="filterStatus">
            {({ field }) => (
              <FormikSelect
                {...field}
                label="Estado:"
                options={statusOptions}
                placeholder=""
                onChange={data => setFieldValue(field.name, data ? data.value : '')}
                isClearable
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
      </Row>
      <Row className="my-3">
        <Col xs={12} md={{ span: 2, offset: 10 }} className="d-flex align-items-end justify-content-end">
          <Button type="submit" className="btn-block btn-primary mb-3">
            FILTRAR
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

const validationSchema = Yup.object().shape({
  dateRange: Yup.array()
    .required('Debes seleccionar un rango de fechas')
    .rangedate(true)
});

const setInitialValues = () => {
  return {
    dateRange: [],
    filterEmployee: '',
    filterTitle: '',
    filterPriority: '',
    filterStatus: ''
  };
};

const handleSubmit = (values, { props }) => {
  const { formRequest } = props;
  formRequest(values);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true
})(CompanyTaskFilter);
